// =====================================================
// REJECTION TYPES API CLIENT
// =====================================================

import { api } from './client';
import type {
  RejectionType,
  RejectionTypeSelect,
  CreateRejectionTypeRequest,
  UpdateRejectionTypeRequest,
} from '../types/catalog-admin';
import type { PaginationQuery, PaginatedResponse } from '../types/pagination';

const BASE_URL = '/rejection-types';

export const rejectionTypesApi = {
  /**
   * Listar todas (sin paginación - mantener compatibilidad)
   */
  getAll: async (includeInactive = false): Promise<RejectionType[]> => {
    const query = includeInactive ? '?includeInactive=true' : '';
    return api.get<RejectionType[]>(`${BASE_URL}${query}`);
  },

  /**
   * Listar con paginación
   */
  getAllPaginated: async (params: PaginationQuery = {}): Promise<PaginatedResponse<RejectionType>> => {
    const searchParams = new URLSearchParams();
    
    if (params.page) searchParams.set('page', params.page.toString());
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.search) searchParams.set('search', params.search);
    if (params.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    if (params.includeInactive) searchParams.set('includeInactive', 'true');

    const queryString = searchParams.toString();
    const url = `${BASE_URL}/paginated${queryString ? `?${queryString}` : ''}`;
    
    return api.get<PaginatedResponse<RejectionType>>(url);
  },

  /**
   * Obtener lista para select (solo activas)
   */
  getForSelect: async (): Promise<RejectionTypeSelect[]> => {
    return api.get<RejectionTypeSelect[]>(`${BASE_URL}/select`);
  },

  /**
   * Obtener por ID
   */
  getById: async (id: string): Promise<RejectionType> => {
    return api.get<RejectionType>(`${BASE_URL}/${id}`);
  },

  /**
   * Crear
   */
  create: async (data: CreateRejectionTypeRequest): Promise<RejectionType> => {
    return api.post<RejectionType>(BASE_URL, data);
  },

  /**
   * Actualizar
   */
  update: async (id: string, data: UpdateRejectionTypeRequest): Promise<RejectionType> => {
    return api.patch<RejectionType>(`${BASE_URL}/${id}`, data);
  },

  /**
   * Desactivar (soft delete)
   */
  deactivate: async (id: string): Promise<RejectionType> => {
    return api.delete<RejectionType>(`${BASE_URL}/${id}`);
  },

  /**
   * Reactivar
   */
  reactivate: async (id: string): Promise<RejectionType> => {
    return api.patch<RejectionType>(`${BASE_URL}/${id}/reactivate`);
  },
  /**
   * Tipos de rechazo asignados al centro activo (para el modal de rechazo en presupuestos)
   */
  getForSite: async (): Promise<{ id: string; name: string; requiresNote: boolean }[]> => {
    return api.get('/rejection-types/for-site');
  },

  /**
   * Centros de la empresa con flag isAssigned para un tipo (ADMIN)
   */
  getSiteAssignments: async (id: string): Promise<{ id: string; code: string; name: string; isAssigned: boolean }[]> => {
    return api.get(`/rejection-types/${id}/sites`);
  },

  /**
   * Actualizar centros asignados a un tipo (ADMIN) — usa PUT
   */
  updateSiteAssignments: async (id: string, siteIds: string[]): Promise<{ id: string; code: string; name: string; isAssigned: boolean }[]> => {
    const token = localStorage.getItem('imi_access_token') || '';
    const response = await fetch('/api/v1/rejection-types/' + id + '/sites', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
      body: JSON.stringify({ siteIds }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Error al actualizar' }));
      throw new Error(err.message || 'Error al actualizar asignaciones');
    }
    return response.json();
  },

  /**
   * IDs de tipos asignados a un centro (para modal bulk — ADMIN)
   */
  getAssignedTypeIds: async (siteId: string): Promise<string[]> => {
    return api.get(`/rejection-types/site-types/${siteId}`);
  },

  /**
   * Asignación masiva: actualiza todos los tipos de un centro (ADMIN)
   */
  bulkAssignToSite: async (siteId: string, rejectionTypeIds: string[]): Promise<void> => {
    const token = localStorage.getItem('imi_access_token') || '';
    const response = await fetch('/api/v1/rejection-types/site-types/' + siteId, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
      body: JSON.stringify({ siteIds: rejectionTypeIds }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Error al guardar' }));
      throw new Error(err.message || 'Error al guardar asignación masiva');
    }
  },
};

export default rejectionTypesApi;
